import React from 'react';
import { TrainScheduleTableProps, TrainScheduleItem } from '../../types';
import '../../../styles/TrainScheduleTable.css';

const formatDuration = (duration: string): string => {
  if (!duration) return '-';
  const parts = duration.split(':');
  if (parts.length === 2) {
    const hours = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')} hrs`;
  }
  return duration;
};

const formatRunningDays = (train: TrainScheduleItem): string => {
  // If runningDays string is already provided, use it
  if (train.runningDays) return train.runningDays;

  // Check if any running day fields exist
  const hasRunningFields = train.runningSun !== undefined || train.runningMon !== undefined;
  if (!hasRunningFields) return '-';

  const days: string[] = [];
  if (train.runningMon === 'Y') days.push('Monday');
  if (train.runningTue === 'Y') days.push('Tuesday');
  if (train.runningWed === 'Y') days.push('Wednesday');
  if (train.runningThu === 'Y') days.push('Thursday');
  if (train.runningFri === 'Y') days.push('Friday');
  if (train.runningSat === 'Y') days.push('Saturday');
  if (train.runningSun === 'Y') days.push('Sunday');

  // If all days, return "Daily"
  if (days.length === 7) return 'Daily';

  // If no days, return dash
  if (days.length === 0) return '-';

  return days.join(', ');
};

export default function TrainScheduleTable({ title, seoTitle, headers, list, type }: TrainScheduleTableProps) {
  if (!list || list.length === 0) {
    return null;
  }

  const titleClass = seoTitle ? 'trainScheduleTitle hasSubtitle' : 'trainScheduleTitle';

  return (
    <div className="trainScheduleContainer">
      <h2 className={titleClass}>{title}</h2>
      {seoTitle && <p className="trainScheduleSeoTitle">{seoTitle}</p>}
      <div className="trainScheduleTableOuter">
        <div className="trainScheduleTableWrapper">
          <table className="trainScheduleTable">
            <thead>
              <tr>
                <th>{headers.trainName}</th>
                <th className="trainScheduleCenter">{headers.dayOfDeparture}</th>
                <th>{headers.originStation}</th>
                <th className="trainScheduleCenter">{headers.timeOfDeparture}</th>
                <th>{headers.destinationStation}</th>
                <th className="trainScheduleCenter">{headers.timeOfArrival}</th>
                <th className="trainScheduleCenter">{headers.totalTime}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((train, index) => {
                const originStation =
                  train.sourceStnName || train.fromStnName || train.fromCityName || train.fromStnCode || '-';
                const destStation =
                  train.destinationStnName || train.toStnName || train.toCityName || train.toStnCode || '-';
                const departureDay = type === 'daily' ? 'Daily' : formatRunningDays(train);

                return (
                  <tr key={`${train.trainNumber}-${index}`}>
                    <td>
                      <div className="trainScheduleTrainName">{train.trainName}</div>
                      <div className="trainScheduleTrainNumber">({train.trainNumber})</div>
                    </td>
                    <td className="trainScheduleCenter">{departureDay}</td>
                    <td>{originStation}</td>
                    <td className="trainScheduleCenter">{train.departureTime}</td>
                    <td>{destStation}</td>
                    <td className="trainScheduleCenter">{train.arrivalTime}</td>
                    <td className="trainScheduleCenter">{formatDuration(train.duration || '')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
